import {type PaymentMethodCreateParams} from '@stripe/stripe-js';
import {$qsAll} from '../../../@shared/ts/dom';
import {formatAddress} from 'localized-address-format';

/**
 * Gets or sets the value of a select element.
 */
function selectGetSet($select: HTMLSelectElement, value?: string, changeEvent = false): string {
	// Set
	if (value !== undefined && typeof value === 'string') {
		$select.value = value;
		if (changeEvent) {
			$select.dispatchEvent(new Event('change', {bubbles: true}));
		}
	}

	// Get
	return $select.selectedOptions[0]?.value ?? '';
}

/**
 * Gets or sets the value of a input[type="checkbox"] element.
 */
function checkboxGetSet($checkbox: HTMLInputElement, value?: boolean, changeEvent = false): string {
	// Set
	if (value !== undefined && typeof value === 'boolean') {
		$checkbox.checked = value;

		if (changeEvent) {
			$checkbox.dispatchEvent(new Event('change', {bubbles: true}));
		}
	}

	// Get
	return $checkbox.checked ? $checkbox.value : '';
}

/**
 * Gets or sets the selected value of a input[type="radio"] element group.
 */
function radioGetSet($radios: HTMLInputElement[], value?: string, _changeEvent = false): string {
	// Set
	if (value !== undefined && typeof value === 'string') {
		throw new Error('Radio input SET not implemented');
	}

	// Get
	for (const radioField of $radios) {
		if ('checked' in radioField && radioField.checked) {
			return radioField.value;
		}
	}

	return '';
}

/**
 * Gets or sets the value of a input[type="text"] or text like input element.
 */
function inputGetSet($input: HTMLInputElement, value?: string, changeEvent = false): string {
	// SET text,phone,tel,email,date,time,... inputs
	if (value !== undefined && typeof value === 'string') {
		$input.value = value;

		if (changeEvent) {
			$input.dispatchEvent(new Event('change', {bubbles: true}));
		}
	}

	// GET text,phone,tel,email,date,time,... inputs
	return $input.value;
}

/**
 * HTML input field getter/setter factory.
 */
function fieldGetSet(selector: string) {
	const getField = () => $qsAll<HTMLInputElement | HTMLSelectElement>(selector);

	return (value?: string | boolean, changeEvent = false): string => {
		const fields = getField();
		const field = fields[0];

		if (!fields.length || !field) {
			return '';
		}

		// Select input
		if (field.nodeName === 'SELECT' && 'selectedOptions' in field) {
			return selectGetSet(field, value as string, changeEvent);
		}

		// Checkbox input
		if (field.nodeName === 'INPUT' && field.type === 'checkbox' && 'checked' in field) {
			return checkboxGetSet(field, value as boolean, changeEvent);
		}

		// Radio input
		if (field.nodeName === 'INPUT' && field.type === 'radio' && 'checked' in field) {
			return radioGetSet(fields as HTMLInputElement[], value as string, changeEvent);
		}

		// All other input types. Ex: text,phone,tel,email,date,time,...
		return inputGetSet(field as HTMLInputElement, value as string, changeEvent);
	};
}

/**
 * Always returns the most up to date customer state
 */
export const PeachPayCustomer = {
	billing: {
		email: fieldGetSet('#pp-billing-form [name="billing_email"]'),
		fullName() {
			const first = PeachPayCustomer.billing.firstName();
			const last = PeachPayCustomer.billing.lastName();

			let fullName = '';
			if (first) {
				fullName += first;
			}

			if (first && last) {
				fullName += ' ';
			}

			if (last) {
				fullName += last;
			}

			return fullName;
		},
		firstName: fieldGetSet('#pp-billing-form [name="billing_first_name"]'),
		lastName: fieldGetSet('#pp-billing-form [name="billing_last_name"]'),
		phone: fieldGetSet('#pp-billing-form [name="billing_phone"]'),
		company: fieldGetSet('#pp-billing-form [name="billing_company"]'),
		address1: fieldGetSet('#pp-billing-form [name="billing_address_1"]'),
		address2: fieldGetSet('#pp-billing-form [name="billing_address_2"]'),
		city: fieldGetSet('#pp-billing-form [name="billing_city"]'),
		postal: fieldGetSet('#pp-billing-form [name="billing_postcode"]'),
		state: fieldGetSet('#pp-billing-form [name="billing_state"]'),
		country: fieldGetSet('#pp-billing-form [name="billing_country"]'),
		formattedAddress() {
			return formatAddress({
				postalCountry: PeachPayCustomer.billing.country(),
				administrativeArea: PeachPayCustomer.billing.state(),
				locality: PeachPayCustomer.billing.city(),
				postalCode: PeachPayCustomer.billing.postal(),
				organization: PeachPayCustomer.billing.company(),
				name: PeachPayCustomer.billing.fullName(),
				addressLines: [
					PeachPayCustomer.billing.address1(),
					PeachPayCustomer.billing.address2(),
				],
			});
		},
	},
	shipToDifferentAddress: fieldGetSet('#pp-shipping-form [name="ship_to_different_address"]'),
	shipping: {
		email: fieldGetSet('#pp-shipping-form [name="shipping_email"]'),
		fullName() {
			const first = PeachPayCustomer.shipping.firstName();
			const last = PeachPayCustomer.shipping.lastName();

			let fullName = '';
			if (first) {
				fullName += first;
			}

			if (first && last) {
				fullName += ' ';
			}

			if (last) {
				fullName += last;
			}

			return fullName;
		},
		firstName: fieldGetSet('#pp-shipping-form [name="shipping_first_name"]'),
		lastName: fieldGetSet('#pp-shipping-form [name="shipping_last_name"]'),
		phone: fieldGetSet('#pp-shipping-form [name="shipping_phone"]'),
		company: fieldGetSet('#pp-shipping-form [name="shipping_company"]'),
		address1: fieldGetSet('#pp-shipping-form [name="shipping_address_1"]'),
		address2: fieldGetSet('#pp-shipping-form [name="shipping_address_2"]'),
		city: fieldGetSet('#pp-shipping-form [name="shipping_city"]'),
		postal: fieldGetSet('#pp-shipping-form [name="shipping_postcode"]'),
		state: fieldGetSet('#pp-shipping-form [name="shipping_state"]'),
		country: fieldGetSet('#pp-shipping-form [name="shipping_country"]'),
		formattedAddress() {
			return formatAddress({
				postalCountry: PeachPayCustomer.shipping.country(),
				administrativeArea: PeachPayCustomer.shipping.state(),
				locality: PeachPayCustomer.shipping.city(),
				postalCode: PeachPayCustomer.shipping.postal(),
				organization: PeachPayCustomer.shipping.company(),
				name: PeachPayCustomer.shipping.fullName(),
				addressLines: [
					PeachPayCustomer.shipping.address1(),
					PeachPayCustomer.shipping.address2(),
				],
			});
		},
	},
	stripeBillingDetails() {
		const details = {
			name: PeachPayCustomer.billing.fullName(),
			email: PeachPayCustomer.billing.email(),
			phone: PeachPayCustomer.billing.phone(),
			address: {
				city: PeachPayCustomer.billing.city(),
				country: PeachPayCustomer.billing.country(),
				line1: PeachPayCustomer.billing.address1(),
				line2: PeachPayCustomer.billing.address2(),
				postal_code: PeachPayCustomer.billing.postal(),
				state: PeachPayCustomer.billing.state(),
			},
		} as PaymentMethodCreateParams.BillingDetails;

		if (details.name === '') {
			delete details.name;
		}

		if (details.email === '') {
			delete details.email;
		}

		if (details.phone === '') {
			delete details.phone;
		}

		if (details.address?.city === '') {
			delete details.address.city;
		}

		if (details.address?.country === '') {
			delete details.address.country;
		}

		if (details.address?.line1 === '') {
			delete details.address.line1;
		}

		if (details.address?.line2 === '') {
			delete details.address.line2;
		}

		if (details.address?.postal_code === '') {
			delete details.address.postal_code;
		}

		if (details.address?.state === '') {
			delete details.address.state;
		}

		if (Object.keys(details.address ?? {}).length === 0) {
			delete details.address;
		}

		return details;
	},
	stripeShippingDetails: () => ({
		name: PeachPayCustomer.shipping.fullName(),
		phone: PeachPayCustomer.shipping.phone(),
		address: {
			city: PeachPayCustomer.shipping.city(),
			country: PeachPayCustomer.shipping.country(),
			line1: PeachPayCustomer.shipping.address1(),
			line2: PeachPayCustomer.shipping.address2(),
			postal_code: PeachPayCustomer.shipping.postal(),
			state: PeachPayCustomer.shipping.state(),
		},
	}),
};
